import { resolveAssetUrl } from '../config';
import { getWorkspaceTheme } from '../utils/workspaceTheme';
import React, { useEffect, useState } from 'react';
import {
  AppBar, Box, CssBaseline, Drawer, IconButton, List, ListItem, Tooltip, Avatar,
  ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography,
  Menu, MenuItem, Divider, useMediaQuery, useTheme, Button,
} from '@mui/material';
import {
  Dashboard as DashboardIcon, Map as MapIcon,
  DirectionsCar as VehiclesIcon, Assignment as RequestIcon,
  CarRental as FleetIcon,
  Logout, Timeline, People as PeopleIcon, Shield as ShieldIcon,
  Menu as MenuIcon, MenuOpen as MenuOpenIcon, Settings as AdminIcon, Visibility as VisibilityIcon,
  ReceiptLong as InvoiceIcon,
  ExitToApp as ExitIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeContext } from '../contexts/ThemeContext';
import NotificationBell from '../components/NotificationBell';
import ThemeSettingsButton from '../components/ThemeSettingsButton';


import BillingGraceBanner from '../components/BillingGraceBanner';
import BillingLockScreen  from '../components/BillingLockScreen';
import DeviceProtectionNotice from '../components/DeviceProtectionNotice';

import { MenuItem as MenuItemType } from '../types';

const drawerWidth = 264;

const menuItems: MenuItemType[] = [

  {
    text: 'Dashboard & Analytics',
    icon: <DashboardIcon />,
    path: '/dashboard',
    hasSubItems: true,
    subItems: [
      { text: 'Dashboard', path: '/dashboard' },
      { text: 'Analytics', path: '/analytics' },
    ],
  },
  { text: 'Map', icon: <MapIcon />, path: '/map', hasSubItems: false },
  {
    text: 'Trip Playback', icon: <Timeline />, path: '/trip-playback',
    hasSubItems: false,
    requiredRoles: ['company_admin', 'admin', 'fleet_manager'],
  },
  { text: 'Vehicles & Assets', icon: <VehiclesIcon />, path: '/devices', hasSubItems: false },
  { text: 'Fleet Management', icon: <FleetIcon />, path: '/fleet-management', hasSubItems: false },
  { text: 'Vehicle Requests', icon: <RequestIcon />, path: '/vehicle-requests', hasSubItems: false },
  { text: 'Drivers', icon: <PeopleIcon />, path: '/drivers', hasSubItems: false },
  { text: 'Invoices', icon: <InvoiceIcon />, path: '/invoices', hasSubItems: false },
  {
    text: 'Geofencing', icon: <ShieldIcon />, path: '/geofencing',
    hasSubItems: false,
    requiredRoles: ['company_admin', 'admin', 'fleet_manager'],
  },
  {
    text: 'Administration', icon: <AdminIcon />, path: '/admin',
    hasSubItems: true,
    requiredRoles: ['company_admin', 'fleet_manager'],
    subItems: [
      { text: 'Users', path: '/admin/users', roles: ['company_admin', 'fleet_manager'] },
      { text: 'Company Settings', path: '/admin/company', roles: ['company_admin'] },
    ],
  },
];

interface CompanyLayoutProps { children: React.ReactNode; }

const CompanyLayout: React.FC<CompanyLayoutProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('CompanyLayout:sidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('CompanyLayout:sidebarCollapsed', String(sidebarCollapsed));
    } catch {
      // The toggle still works when browser storage is unavailable.
    }
  }, [sidebarCollapsed]);

  const { user, company, logout, isRole, isImpersonating, stopImpersonating } = useAuth();
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colors = getWorkspaceTheme(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => { setMobileOpen(false); }, [location.pathname, isMobile]);
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }, [location.pathname]);
  const desktopDrawerWidth = sidebarCollapsed ? 0 : drawerWidth;

  const handleDrawerToggle = () => {
    setMobileOpen(prev => !prev);
  };

  const getLogoPath = () => {
    if (company?.logo) {
      if (company.logo.startsWith('http://') || company.logo.startsWith('https://')) {
        return company.logo;
      }
      return resolveAssetUrl(company.logo);
    }
    return '/Scetru-Logo-white.png';
  };

  const isMap = location.pathname === '/map';
  const navigationGroups = [
    { label: 'OVERVIEW', paths: ['/dashboard', '/analytics'] },
    { label: 'FLEET OPERATIONS', paths: ['/map', '/trip-playback', '/devices', '/fleet-management', '/vehicle-requests', '/drivers', '/geofencing'] },
    { label: 'WORKSPACE', paths: ['/invoices', '/admin/users', '/admin/company'] },
  ];
  const navigationItems = menuItems
    .filter(item => !item.requiredRoles || item.requiredRoles.some(role => isRole(role)))
    .flatMap(item => item.subItems
      ? item.subItems.filter(sub => !sub.roles || sub.roles.some(role => isRole(role))).map(sub => ({ text: sub.text, path: sub.path, icon: item.icon }))
      : [{ text: item.text, path: item.path, icon: item.icon }]);
  const pageTitle = navigationItems.find(item => item.path === location.pathname)?.text || 'Fleet workspace';


  const drawer = (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', bgcolor: colors.sidebar }}>
      <Box sx={{ px: 3, height: 80, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="img" src="/Scetru-Logo-white.png" alt="Scetru" sx={{ width: 112, height: 38, objectFit: 'contain' }} />
        <Typography sx={{ color: colors.sidebarMuted, fontSize: 10, letterSpacing: '0.15em', ml: 'auto' }}>FLEET</Typography>
      </Box>
      <Box sx={{ mx: 2, mb: 1, p: 1.5, display: 'flex', gap: 1.25, alignItems: 'center', bgcolor: colors.sidebarHover, border: `1px solid ${colors.sidebarBorder}`, borderRadius: 2.5 }}>
          <Box component="img" src={getLogoPath()} alt="" sx={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 1.5 }} onError={event => { event.currentTarget.src = '/Scetru-Logo-white.png'; }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ color: colors.sidebarText, fontWeight: 650, fontSize: 13 }}>{company?.name || 'Fleet workspace'}</Typography>
            <Typography sx={{ color: colors.sidebarMuted, fontSize: 11 }}>Company workspace</Typography>
          </Box>
        </Box>
      <Box sx={{ flexGrow: 1, px: 1.5, pb: 2 }}>
        {navigationGroups.map(group => {
          const items = navigationItems.filter(item => group.paths.includes(item.path));
          if (!items.length) return null;
          return <Box key={group.label} sx={{ mt: 2.5 }}>
            <Typography sx={{ px: 1.5, mb: 1, color: colors.sidebarMuted, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em' }}>{group.label}</Typography>
            <List disablePadding>
              {items.map(item => <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton selected={location.pathname === item.path}
                  onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                  sx={{ minHeight: 44, px: 1.5, borderRadius: 2, color: colors.sidebarText,
                    '& .MuiListItemIcon-root': { color: colors.sidebarMuted },
                    '&:hover': { bgcolor: colors.sidebarHover, color: '#FFFFFF' },
                    '&.Mui-selected': { bgcolor: colors.selected, color: '#FFFFFF', boxShadow: '0 4px 12px #00000018',
                      '&:hover': { bgcolor: colors.selectedHover }, '& .MuiListItemIcon-root': { color: '#FFFFFF' } },
                  }}>
                  <ListItemIcon sx={{ minWidth: 34, '& svg': { fontSize: 19 } }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 12.5, fontWeight: location.pathname === item.path ? 650 : 450 }} />
                </ListItemButton>
              </ListItem>)}
            </List>
          </Box>;
        })}
      </Box>
      <Box sx={{ mx: 3, py: 2.5, borderTop: `1px solid ${colors.sidebarBorder}` }}>
        <Typography sx={{ color: colors.sidebarMuted, fontSize: 10 }}>Powered by <Box component="span" sx={{ color: colors.sidebarText, fontWeight: 700 }}>Scetru</Box></Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <CssBaseline />

      {/* ── Impersonation Banner ─────────────────────────────────────────────── */}
      {isImpersonating && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0,
          zIndex: (t) => t.zIndex.drawer + 2,
          background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
          px: 2, py: 0.75,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon sx={{ fontSize: 16, color: '#000' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#000' }}>
              Viewing as {user?.firstName} {user?.lastName}
              <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 600, ml: 0.75, color: 'rgba(0,0,0,0.6)' }}>
                ({company?.name})
              </Typography>
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<ExitIcon fontSize="small" />}
            onClick={stopImpersonating}
            sx={{
              fontWeight: 800, fontSize: '0.72rem', textTransform: 'none',
              background: 'rgba(0,0,0,0.12)', color: '#000', borderRadius: 1.5,
              px: 1.5, py: 0.25,
              '&:hover': { background: 'rgba(0,0,0,0.2)' },
            }}
          >
            Exit Impersonation
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', mt: isImpersonating ? '36px' : 0 }}>
        <AppBar position="fixed" sx={{
          top: isImpersonating ? '36px' : 0,
          width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
          ml: { md: `${desktopDrawerWidth}px` },
          background: colors.header,
          color: 'text.primary',
          boxShadow: 'none', borderBottom: '1px solid',
          borderColor: colors.headerBorder,
          zIndex: (t) => t.zIndex.drawer + 1,
          '& .MuiIconButton-root': { color: colors.avatarText },

        }}>
          <Toolbar sx={{ justifyContent: 'space-between', gap: { xs: 0.5, sm: 1.5 }, px: { xs: 1.5, sm: 3 } }}>
            <Tooltip title={isMobile ? 'Open sidebar' : sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <IconButton
                edge="start"
                aria-label={isMobile ? 'Open sidebar' : sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-expanded={isMobile ? mobileOpen : !sidebarCollapsed}
                onClick={() => {
                  if (isMobile) setMobileOpen(open => !open);
                  else setSidebarCollapsed(collapsed => !collapsed);
                }}
              >
                {!isMobile && !sidebarCollapsed ? <MenuOpenIcon /> : <MenuIcon />}
              </IconButton>
            </Tooltip>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 700 }}>{pageTitle}</Typography>
              <Typography noWrap sx={{ fontSize: 10, color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>Scetru Fleet / Company workspace</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 1 }, ml: 'auto', flexShrink: 0 }}>
              <ThemeSettingsButton />
              <NotificationBell />
              <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' }, fontSize: 12, fontWeight: 600, ml: 1.5 }}>
                {user?.firstName} {user?.lastName}
                <Typography component="span" variant="caption" sx={{ display: 'block', fontSize: 10, textTransform: 'capitalize', color: 'text.secondary' }}>
                  ({user?.role?.replace('_', ' ')})
                </Typography>
              </Typography>
              <IconButton aria-label="Open account menu" size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <Avatar sx={{ width: 34, height: 34, bgcolor: colors.avatar, color: colors.avatarText, fontSize: 12, fontWeight: 700 }}>{user?.firstName?.[0]}{user?.lastName?.[0]}</Avatar>
              </IconButton>
            </Box>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{ '& .MuiPaper-root': { backgroundColor: 'background.paper' } }}>
              <MenuItem disabled><Typography variant="body2">{user?.firstName} {user?.lastName}</Typography></MenuItem>
              <MenuItem disabled><Typography variant="body2" sx={{ color: 'text.secondary' }}>{user?.email}</Typography></MenuItem>
              <MenuItem disabled><Typography variant="body2" sx={{ color: 'text.secondary' }}>{company?.name}</Typography></MenuItem>
              <Divider />
              {isImpersonating && (
                <MenuItem onClick={stopImpersonating} sx={{ color: '#f59e0b' }}>
                  <ListItemIcon><ExitIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon>
                  Exit Impersonation
                </MenuItem>
              )}
              <MenuItem onClick={async () => { await logout(); navigate('/login'); }}>
                <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component="nav" sx={{ width: { md: desktopDrawerWidth }, flexShrink: { md: 0 } }}>
          <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              zIndex: (t) => t.zIndex.drawer + 2,
              '& .MuiDrawer-paper': {
                width: `min(${drawerWidth}px, 88vw)`,
                top: isImpersonating ? '36px' : 0,
                backgroundColor: colors.sidebar,
              },
            }}>
            {drawer}
          </Drawer>
          <Drawer variant="permanent"
            sx={{
              display: { xs: 'none', md: sidebarCollapsed ? 'none' : 'block' },
              '& .MuiDrawer-paper': {
                width: `min(${drawerWidth}px, 88vw)`,
                top: isImpersonating ? '36px' : 0,
                height: isImpersonating ? 'calc(100% - 36px)' : '100%',
                borderRight: '1px solid',
                borderColor: colors.sidebarBorder,
                backgroundColor: colors.sidebar,
              },
            }}
            open>
            {drawer}
          </Drawer>
        </Box>

        <Box component="main" className="workspace-content" sx={{
        minWidth: 0, maxWidth: '100%',
          flexGrow: 1, p: { xs: 1.5, sm: 3 },
          width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
          minHeight: '100dvh',
          backgroundColor: 'background.default',
          color: 'text.primary',
          // Map overlays retain their own map-specific contrast.
          ...(isMap ? {
            '& .MuiPaper-root': { backgroundColor: 'rgba(45,45,45,0.9)', color: '#fff' },
          } : {}),
        }}>
        <Toolbar />
          <Box sx={{ mx: { xs: -1.5, sm: -3 }, mt: { xs: -1.5, sm: -3 }, mb: 2 }}>
            <BillingGraceBanner />
          </Box>
          <DeviceProtectionNotice />
          <BillingLockScreen>
            {children}
          </BillingLockScreen>
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyLayout;
